/**
 * SmartBlockTracker — Full tracker dashboard page.
 *
 * Combines daily input form, score gauge, CSK radar, trend chart, streak,
 * and cluster history into a single view.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  getDashboard,
  submitBlock,
  getEnrollments,
  enrollTracker,
  getTrackerTypes,
} from '@/lib/smartblocksApi';
import type {
  DashboardData,
  TrackerType,
  Enrollment,
  BlockSubmitDiagnostics,
} from '@/lib/smartblocksApi';
import { ScoreGauge } from './ScoreGauge';
import { CSKRadar } from './CSKRadar';
import { TrendChart } from './TrendChart';
import { BlockInputForm } from './BlockInputForm';

type SmartBlockTrackerProps = {
  /** tracker type id from the tool's meta.tracker_type_id */
  trackerTypeId: string;
  /** role for context */
  role: 'prospect' | 'coach' | 'student';
};

type ViewTab = 'dashboard' | 'input';

export function SmartBlockTracker({ trackerTypeId, role }: SmartBlockTrackerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [trackerType, setTrackerType] = useState<TrackerType | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [activeTab, setActiveTab] = useState<ViewTab>('dashboard');
  const [submitting, setSubmitting] = useState(false);
  const [diagnostics, setDiagnostics] = useState<BlockSubmitDiagnostics | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load tracker type definition
      const types = await getTrackerTypes();
      const tt = types.find((t) => t.id === trackerTypeId);
      if (!tt) {
        setError('Tracker type not found');
        return;
      }
      setTrackerType(tt);

      // Check enrollment
      const enrollments = await getEnrollments();
      const en = enrollments.find((e) => e.tracker_type_id === trackerTypeId);

      if (!en) {
        // Auto-enroll on first visit
        const newEnrollment = await enrollTracker(trackerTypeId);
        setEnrollment(newEnrollment);
      } else {
        setEnrollment(en);
      }

      // Load dashboard
      const dash = await getDashboard(trackerTypeId);
      setDashboard(dash);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tracker data');
    } finally {
      setLoading(false);
    }
  }, [trackerTypeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmitBlock = async (bitblocks: Record<string, Record<string, number>>) => {
    setSubmitting(true);
    setSubmitSuccess(false);
    try {
      const result = await submitBlock({
        tracker_type_id: trackerTypeId,
        bitblocks,
      });
      setDiagnostics(result.diagnostics);
      setSubmitSuccess(true);
      // Refresh dashboard
      const dash = await getDashboard(trackerTypeId);
      setDashboard(dash);
      // Switch to dashboard to show results
      setActiveTab('dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit block');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        <div className="h-8 w-48 animate-pulse rounded-lg bg-slate-200" />
        <div className="h-48 animate-pulse rounded-xl bg-slate-100" />
        <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={loadData}
          className="mt-3 rounded-lg bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  const baselines =
    enrollment?.baselines ?? trackerType?.default_baselines ?? {};

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-cocoa">
            {trackerType?.label || 'SmartBlock Tracker'}
          </h2>
          <p className="text-sm text-cocoa/60">{trackerType?.description}</p>
        </div>
        {dashboard && (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
              {dashboard.streak_days} day streak
            </span>
          </div>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === 'dashboard'
              ? 'bg-white text-cocoa shadow-sm'
              : 'text-cocoa/60 hover:text-cocoa'
          }`}
        >
          Dashboard
        </button>
        <button
          onClick={() => { setActiveTab('input'); setSubmitSuccess(false); }}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === 'input'
              ? 'bg-white text-cocoa shadow-sm'
              : 'text-cocoa/60 hover:text-cocoa'
          }`}
        >
          Log Today
        </button>
      </div>

      {/* Success banner */}
      {submitSuccess && activeTab === 'dashboard' && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          Block sealed and submitted! Your scores have been updated.
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Dashboard view */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-4">
          {/* Score + CSK row */}
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Score gauge card */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="mb-2 text-sm font-semibold text-cocoa/70">Current Score</h3>
              <ScoreGauge
                score={dashboard.latest_block?.gec0_score ?? 0}
                label="SmartBlock Score"
              />
              {dashboard.latest_block && (
                <div className="mt-2 flex justify-center gap-4 text-xs text-cocoa/60">
                  <span>
                    Momentum:{' '}
                    <span
                      className={
                        dashboard.latest_block.momentum >= 0
                          ? 'text-emerald-600'
                          : 'text-red-500'
                      }
                    >
                      {dashboard.latest_block.momentum >= 0 ? '+' : ''}
                      {Math.round(dashboard.latest_block.momentum * 100)}%
                    </span>
                  </span>
                  <span>
                    Composite: {Math.round(dashboard.latest_block.composite_score * 100)}%
                  </span>
                </div>
              )}
            </div>

            {/* CSK Radar card */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="mb-2 text-sm font-semibold text-cocoa/70">Factor Analysis</h3>
              {dashboard.csk_current ? (
                <CSKRadar factors={dashboard.csk_current} size={180} />
              ) : (
                <div className="flex h-[180px] items-center justify-center text-sm text-cocoa/50">
                  Submit a block to see your factor analysis.
                </div>
              )}
            </div>
          </div>

          {/* Trend chart */}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="mb-3 text-sm font-semibold text-cocoa/70">14-Day Trend</h3>
            <TrendChart data={dashboard.trend} height={200} />
          </div>

          {/* Category breakdown */}
          {dashboard.latest_block && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="mb-3 text-sm font-semibold text-cocoa/70">Category Scores</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {Object.entries(dashboard.latest_block.bitblocks).map(([key, bb]) => {
                  const cat = trackerType?.categories?.find((c) => c.key === key);
                  return (
                    <div key={key} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
                      <span className="text-lg">{cat?.icon || '?'}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-cocoa">{cat?.label || key}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-16 overflow-hidden rounded-full bg-slate-200">
                          <div
                            className={`h-full rounded-full transition-all ${
                              bb.score >= 0.7
                                ? 'bg-emerald-500'
                                : bb.score >= 0.45
                                  ? 'bg-amber-500'
                                  : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.round(bb.score * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-cocoa">
                          {Math.round(bb.score * 100)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cluster history */}
          {dashboard.recent_clusters.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="mb-3 text-sm font-semibold text-cocoa/70">Cluster History</h3>
              <div className="space-y-2">
                {dashboard.recent_clusters.map((cluster) => (
                  <div
                    key={cluster.id}
                    className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-medium text-cocoa">
                        {new Date(cluster.period_start).toLocaleDateString()} -{' '}
                        {new Date(cluster.period_end).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-cocoa/50">
                        {cluster.block_count} blocks
                        {cluster.triggered_recalibration && (
                          <span className="ml-2 text-amber-600">Recalibration triggered</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-600">
                        {Math.round(cluster.avg_gec0 * 100)}%
                      </p>
                      <p className={`text-xs ${cluster.momentum_score >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {cluster.momentum_score >= 0 ? '+' : ''}
                        {Math.round(cluster.momentum_score * 100)}% momentum
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No data prompt */}
          {!dashboard.latest_block && (
            <div className="rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 py-8 text-center">
              <p className="text-lg font-medium text-cocoa">Welcome to your tracker!</p>
              <p className="mt-1 text-sm text-cocoa/60">
                Start by logging today's data to see your scores.
              </p>
              <button
                onClick={() => setActiveTab('input')}
                className="mt-4 rounded-lg bg-emerald-600 px-6 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Log Today
              </button>
            </div>
          )}
        </div>
      )}

      {/* Input view */}
      {activeTab === 'input' && trackerType && (
        <BlockInputForm
          categories={trackerType.categories}
          baselines={baselines}
          onSubmit={handleSubmitBlock}
          lastDiagnostics={diagnostics}
          loading={submitting}
        />
      )}
    </div>
  );
}

export default SmartBlockTracker;
